import { TestSuite } from 'store/src/lib/tests/entity';
import { RatingTypes } from './types';

export const ConvertResultToIndex = (result: number) => {
  if (result > 0 && result <= 5) {
    return Math.floor(result) - 1;
  } else {
    return undefined;
  }
};

export const convertAverage = (average: number) =>
  Math.floor(isNaN(average) ? 0 : average ?? 0);

export const convertRatingToScore = (rating: string) => {
  const ratings = ['very_poor', 'poor', 'fair', 'good', 'very_good'];
  const index = ratings.indexOf(rating);
  return index === -1 ? undefined : index + 1;
};

export const convertScoreToRating = (score: number): RatingScale => {
  const ratings = ['very_poor', 'poor', 'fair', 'good', 'very_good'];
  return ratings[score - 1] as RatingScale;
};

export const convertRatingToIndex = (rating: string) => {
  const score = convertRatingToScore(rating);
  return score ? score - 1 : undefined;
};

export function convertRatingToGroupName(ratingType: RatingTypes) {
  switch (ratingType) {
    case 'compliance':
    case 'compliance_a':
    case 'compliance_b':
      return 'compliance';
    case 'condition':
      return 'condition';
    default:
      return 'generic';
  }
}

export const ratingMix = (ratingTypes: RatingTypes[]) => {
  const uniqueRatingTypes = new Set(ratingTypes);

  // Handle edge cases first
  if (uniqueRatingTypes.size === 0) return null;
  if (uniqueRatingTypes.size === 1 && uniqueRatingTypes.has('generic'))
    return 'no_rating';

  // Remove generic type as it doesn't affect the rating mix
  uniqueRatingTypes.delete('generic');

  // Handle single rating type cases
  if (uniqueRatingTypes.size === 1) {
    const [type] = uniqueRatingTypes;
    if (type === 'compliance') return 'compliance_a';
    if (type === 'compliance_b') return 'compliance_b';
    if (type === 'condition') return 'condition';
    return 'no_rating';
  }

  // Handle multiple rating type combinations
  const hasCompliance = uniqueRatingTypes.has('compliance');
  const hasComplianceB = uniqueRatingTypes.has('compliance_b');
  const hasCondition = uniqueRatingTypes.has('condition');

  const ratingCombinations: [boolean[], RatingTypes][] = [
    [[hasCompliance, hasCondition, hasComplianceB], 'mixed'],
    [[hasCompliance, hasComplianceB], 'compliance_a'],
    [[hasCondition, hasCompliance], 'mixed'],
    [[hasCondition, hasComplianceB], 'frankenstein'],
  ];

  const matchedRating = ratingCombinations.find(([conditions]) =>
    conditions.every((condition) => condition)
  );

  if (!matchedRating) {
    console.debug('unknown rating mix', uniqueRatingTypes);
    return 'unknown';
  }

  return matchedRating[1];
};

/**
 * Returns the label-mode array that getMixedRatingLabel expects, aligned with
 * the resolved type from ratingMix (same as the rating image). Empty ratingTypes
 * returns undefined so getMixedRatingLabel uses its default (both).
 */
export function getLabelTypesForRatingTypes(
  ratingTypes: RatingTypes[]
): ('compliance' | 'condition')[] | undefined {
  if (!ratingTypes?.length) return undefined
  const resolved = ratingMix(ratingTypes)
  if (!resolved || resolved === 'no_rating') return undefined
  if (resolved === 'condition') return ['condition']
  if (resolved === 'compliance' || resolved === 'compliance_a' || resolved === 'compliance_b') {
    return ['compliance']
  }
  return ['compliance', 'condition']
}

export const getBaseTestSuiteType = (testSuiteType: string) => {
  return testSuiteType.split('_')[0];
};

//ratingType to groupName lookup table
const ratingTypeToGroupName: Record<string, string> = {
  compliance: 'Compliance',
  compliance_a: 'Compliance',
  compliance_b: 'Compliance',
  condition: 'Condition',
  mixed: 'Mixed',
  no_rating: 'Not Rated',
};

export const getRatingGroupName = (ratingType: RatingTypes) => {
  return ratingTypeToGroupName[ratingType];
};

type RatingScale =
  | 'very_poor'
  | 'poor'
  | 'fair'
  | 'good'
  | 'very_good'
  | 'no_rating';

const compliance: { [key: string]: string } = {
  very_poor: 'Not compliant',
  poor: 'Not compliant',
  fair: 'Not compliant',
  good: 'Compliant',
  very_good: 'Compliant',
  no_rating: 'No Rating',
};

const condition: { [key: string]: string } = {
  very_poor: 'Very Poor',
  poor: 'Poor',
  fair: 'Fair',
  good: 'Good',
  very_good: 'Very Good',
  no_rating: 'No Rating',
};

export function getOverallRatingLabel(
  rating: RatingScale,
  type = 'compliance'
): string {
  const whatType = typeof type === 'string' ? type : type[0] ?? 'compliance';
  return whatType === 'condition' ? condition[rating] : compliance[rating];
}

export const getMixedRatingLabel = (
  rating: RatingScale,
  type: string[] = ['compliance', 'condition']
): string[] => {
  if (type.length === 1) {
    return [getOverallRatingLabel(rating, type[0])];
  }
  const labels = [...type].sort().reduce((acc, curr) => {
    const label =
      curr === 'compliance'
        ? compliance[rating]
        : curr === 'condition'
        ? condition[rating]
        : null;
    if (label) {
      acc.push(label);
    }
    return acc;
  }, [] as string[]);

  if (rating !== 'no_rating') {
    labels.push(convertRatingToScore(rating)?.toString() + '.00 / 5.00');
  }

  return Array.from(new Set(labels));
};

export function getOverallRating(score: number): RatingScale {
  if (score < 2) return 'very_poor';
  if (score < 3) return 'poor';
  if (score < 4) return 'fair';
  if (score < 5) return 'good';
  if (score >= 5) return 'very_good';
  return 'no_rating';
}

export const getRatingLabel = (testSuite: TestSuite, rating: string) => {
  const ratingType = testSuite?.ratingScale?.[`${rating}_label`];
  return ratingType ?? rating;
};

/**
 * Helper function to convert hex color to RGBA with opacity
 */
function hexToRgba(hex: string, opacity: number): string {
  // Remove # if present
  hex = hex.replace('#', '');

  // Parse hex values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Convert opacity from 0-100 scale to 0-1 scale
  const alpha = opacity / 100;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function useTestSuiteTypeColor(ratingType: RatingTypes, opacity = 100) {
  //compliance: '#16DB65',
  //condition: '#00A6FB',
  //generic: '#d4d4d8',
  const groupName = convertRatingToGroupName(ratingType);

  let hexColor: string;
  if (groupName === 'compliance') {
    hexColor = '#16DB65';
  } else if (groupName === 'condition') {
    hexColor = '#00A6FB';
  } else {
    hexColor = '#09090B';
  }

  return hexToRgba(hexColor, opacity);
}
